import React from 'react';
import AppLoader from '../../shared/AppLoader';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import TuneIcon from '@mui/icons-material/Tune';
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt';
import SettingsIcon from '@mui/icons-material/Settings';
import MessageIcon from '@mui/icons-material/Message';
import FontDownloadIcon from '@mui/icons-material/FontDownload';
import { AI_PROVIDERS } from './shared/widgetConstants';
import { AiProviderButton, AiSummaryInlineBlock } from './shared/widgetHelpers';
import FontPicker from '../Editor/components/FontPicker';
import useWidgetEditor from './shared/useWidgetEditor';
import WidgetEditorShell from './shared/WidgetEditorShell';

export default function AiSummaryEditor({ site }) {
  const {
    siteId,
    widgetId,
    history,
    queryWidgetType,
    widgetType,
    setWidgetType,
    loading,
    saving,
    activeTab,
    setActiveTab,
    validationErrors,
    showErrorDialog,
    setShowErrorDialog,
    rules,
    widget,
    setWidget,
    updateSetting,
    updateField,
    saveWidget,
    fontLibrary,
  } = useWidgetEditor({ site });

  if (loading) return <AppLoader visible={true} />;

  const tabs = [
    { id: 'content', label: 'Content', icon: <ChatBubbleOutlineIcon /> },
    { id: 'channels', label: 'Providers', icon: <MessageIcon /> },
    { id: 'layout', label: 'Layout', icon: <ViewQuiltIcon /> },
    { id: 'targeting', label: 'Behavior', icon: <TuneIcon /> },
    { id: 'basic', label: 'Settings', icon: <SettingsIcon /> },
  ];

  const btnStyle = widget.settings.ai_summary_button_style || 'pill';
  const btnSize  = widget.settings.ai_summary_button_size  || 'medium';
  const align    = widget.settings.ai_summary_alignment    || 'left';
  const activeProviders = AI_PROVIDERS.filter(p => (widget.settings.ai_summary_providers || []).includes(p.value));
  const flexAlign = align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start';
  const cssSelector = widget.settings.ai_summary_css_selector || '';
  const cssPosition = widget.settings.ai_summary_css_position || 'after';

  const ArticleLines = ({ short }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '7px', padding: short ? '0 0 18px 0' : '18px 0 0 0' }}>
      {(short ? [55, 80, 65] : [88, 72, 90, 60, 78, 50]).map((w, i) => (
        <div key={i} style={{ height: '9px', background: '#EBEBEB', borderRadius: '4px', width: `${w}%` }} />
      ))}
    </div>
  );

  const previewContent = (
    <div style={{
      position: 'absolute', inset: 0, overflowY: 'auto', padding: '24px 28px',
      fontFamily: widget.settings.font_family && widget.settings.font_family !== 'inherit'
        ? widget.settings.font_family : 'system-ui, sans-serif',
      display: 'flex', flexDirection: 'column', justifyContent: 'center',
    }}>
      <AiSummaryInlineBlock
        heading={widget.settings.ai_summary_heading}
        providers={activeProviders}
        btnStyle={btnStyle} btnSize={btnSize} flexAlign={flexAlign}
      />
    </div>
  );

  const tabContent = (
    <>
      {activeTab === 'basic' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '0.85em', fontWeight: 'bold', color: '#4F4F4F' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              Widget Internal Name
              <span style={{ fontSize: '11px', color: '#999', fontWeight: 'normal' }}>{widget.name.length}/50</span>
            </div>
            <input
              type="text"
              value={widget.name}
              onChange={e => updateField('name', e.target.value)}
              placeholder="e.g. Sales Support"
              maxLength={50}
              style={{ padding: '12px', borderRadius: '8px', border: '1px solid #D0D0D0', fontSize: '15px' }}
            />
          </label>
        </div>
      )}

      {activeTab === 'channels' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div>
            <span style={{ fontSize: '0.85em', fontWeight: 'bold', color: '#4F4F4F' }}>AI Providers</span>
            <p style={{ fontSize: '12px', color: '#888', margin: '4px 0 0 0' }}>Toggle the providers to show on your widget. Drag to reorder.</p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {AI_PROVIDERS.map(provider => {
              const selected = (widget.settings.ai_summary_providers || []).includes(provider.value);
              return (
                <div
                  key={provider.value}
                  onClick={() => {
                    const current = widget.settings.ai_summary_providers || [];
                    const updated = selected
                      ? current.filter(v => v !== provider.value)
                      : [...current, provider.value];
                    updateSetting('ai_summary_providers', updated);
                  }}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 14px', borderRadius: '10px', cursor: 'pointer', userSelect: 'none',
                    border: `1.5px solid ${selected ? provider.color : '#E5E7EB'}`,
                    background: selected ? `${provider.color}08` : '#FAFAFA',
                    transition: 'all 0.18s',
                    opacity: selected ? 1 : 0.55,
                  }}
                >
                  <AiProviderButton
                    provider={provider}
                    style={widget.settings.ai_summary_button_style || 'pill'}
                    size="small"
                  />
                  <div style={{
                    width: '20px', height: '20px', borderRadius: '50%', flexShrink: 0,
                    border: `2px solid ${selected ? provider.color : '#D1D5DB'}`,
                    background: selected ? provider.color : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'white', fontSize: '11px', fontWeight: 'bold',
                    transition: 'all 0.18s',
                  }}>
                    {selected ? '✓' : ''}
                  </div>
                </div>
              );
            })}
          </div>
          {(!widget.settings.ai_summary_providers || widget.settings.ai_summary_providers.length === 0) && (
            <span style={{ fontSize: '12px', color: '#d32f2f', fontWeight: 'bold' }}>Select at least one provider.</span>
          )}
        </div>
      )}

      {activeTab === 'content' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* AI Summary Heading */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '0.85em', fontWeight: 'bold', color: '#4F4F4F' }}>Heading</span>
              <span style={{ fontSize: '11px', color: '#999', fontWeight: 'normal' }}>{widget.settings.ai_summary_heading?.length || 0}/60</span>
            </div>
            <input
              type="text"
              value={widget.settings.ai_summary_heading}
              maxLength={60}
              onChange={e => updateSetting('ai_summary_heading', e.target.value)}
              placeholder="e.g. Summarize this article with AI"
              style={{ padding: '12px', borderRadius: '8px', border: '1px solid #D0D0D0', fontSize: '14px' }}
            />
          </div>

          <div style={{ height: '1px', background: '#F3F4F6' }}></div>

          {/* Prompt Builder — no raw {url} exposed to user */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div>
              <span style={{ fontSize: '0.85em', fontWeight: 'bold', color: '#4F4F4F' }}>AI Prompt</span>
              <p style={{ fontSize: '12px', color: '#888', margin: '4px 0 0 0' }}>
                Write the instruction you want sent to the AI. The current page URL is always attached automatically.
              </p>
            </div>

            {/* Composed prompt box */}
            <div style={{
              border: '1px solid #D0D0D0', borderRadius: '8px',
              background: 'white', overflow: 'hidden',
            }}>
              {/* Editable instruction */}
              <textarea
                value={(widget.settings.ai_summary_prompt || '').replace(/\n*\{url\}\s*$/m, '').replace(/\{url\}/g, '').trimEnd()}
                onChange={e => {
                  const instruction = e.target.value;
                  updateSetting('ai_summary_prompt', instruction ? `${instruction}\n\n{url}` : '{url}');
                }}
                placeholder="Summarize the following article in a few bullet points:"
                rows={4}
                style={{
                  width: '100%', boxSizing: 'border-box',
                  padding: '12px', border: 'none', outline: 'none',
                  fontSize: '14px', resize: 'none', fontFamily: 'inherit',
                  background: 'transparent',
                }}
              />
              {/* URL token — always appended, shown read-only */}
              <div style={{
                padding: '8px 12px', borderTop: '1px dashed #E5E7EB',
                background: '#F8FAFF',
                display: 'flex', alignItems: 'center', gap: '8px',
              }}>
                <span style={{ fontSize: '11px', color: '#9CA3AF' }}>automatically appends →</span>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: '5px',
                  padding: '3px 10px', borderRadius: '20px',
                  background: '#EEF2FF', border: '1px solid #C7D2FE',
                  fontSize: '12px', fontWeight: '600', color: '#4F46E5',
                }}>
                  🔗 Current page URL
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'layout' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Placement — CSS Selector only */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div>
              <span style={{ fontSize: '0.85em', fontWeight: 'bold', color: '#4F4F4F' }}>CSS Selector</span>
              <p style={{ fontSize: '12px', color: '#888', margin: '4px 0 0 0' }}>
                Select the CSS selector of the element on your page where you'd like to insert this widget.
              </p>
            </div>
            <textarea
              value={widget.settings.ai_summary_css_selector || ''}
              onChange={e => updateSetting('ai_summary_css_selector', e.target.value)}
              placeholder=".classname or #id"
              rows={3}
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '10px 12px', borderRadius: '8px',
                border: '1px solid #D0D0D0', fontSize: '13px',
                fontFamily: 'monospace', background: 'white',
                outline: 'none', resize: 'vertical',
              }}
            />
            {(!widget.settings.ai_summary_css_selector || !widget.settings.ai_summary_css_selector.trim()) && (
              <span style={{ fontSize: '12px', color: '#d32f2f', fontWeight: 'bold' }}>CSS Selector is required.</span>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <span style={{ fontSize: '0.85em', fontWeight: 'bold', color: '#4F4F4F' }}>Insert at</span>
              <p style={{ fontSize: '12px', color: '#888', margin: 0 }}>
                Select the position where you'd like to insert the widget relative to the CSS selector element.
              </p>
              <select
                value={widget.settings.ai_summary_css_position || 'after'}
                onChange={e => updateSetting('ai_summary_css_position', e.target.value)}
                style={{
                  padding: '10px 12px', borderRadius: '8px',
                  border: '1px solid #D0D0D0', background: '#fff',
                  fontSize: '14px', cursor: 'pointer',
                }}
              >
                <option value="after">after selected element</option>
                <option value="before">before selected element</option>
                <option value="replace">replace selected element</option>
              </select>
            </div>
          </div>

          <div style={{ height: '1px', background: '#F3F4F6' }}></div>

          {/* Typography */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <span style={{ fontSize: '0.85em', fontWeight: 'bold', color: '#4F4F4F', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FontDownloadIcon style={{ fontSize: '16px', color: '#ED5A29' }} /> Typography
            </span>
            <FontPicker
              customFonts={[
                { label: 'Website Default (Inherit)', value: 'inherit', family: 'inherit' },
                ...fontLibrary.map(f => ({ label: f.font_name, value: f.font_name, family: f.font_name }))
              ]}
              value={widget.settings.font_family || 'inherit'}
              onChange={font => updateSetting('font_family', font)}
            />
            <p style={{ fontSize: '11px', color: '#777', margin: 0 }}>
              Pick a font that matches your website's brand identity.
            </p>
          </div>

          <div style={{ height: '1px', background: '#F3F4F6' }}></div>

          {/* Button Style */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <span style={{ fontSize: '0.85em', fontWeight: 'bold', color: '#4F4F4F' }}>Button Style</span>
            <div style={{ display: 'flex', gap: '8px' }}>
              {[
                { value: 'outlined', label: 'Outlined' },
                { value: 'filled', label: 'Filled' },
                { value: 'pill', label: 'Pill' },
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => updateSetting('ai_summary_button_style', opt.value)}
                  style={{
                    flex: 1, padding: '8px 4px', borderRadius: opt.value === 'pill' ? '20px' : '7px', cursor: 'pointer', fontSize: '12px', fontWeight: '700', border: 'none',
                    background: widget.settings.ai_summary_button_style === opt.value ? '#ED5A29' : '#F3F4F6',
                    color: widget.settings.ai_summary_button_style === opt.value ? 'white' : '#6B7280',
                    transition: 'all 0.2s'
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Button Size */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <span style={{ fontSize: '0.85em', fontWeight: 'bold', color: '#4F4F4F' }}>Button Size</span>
            <div style={{ display: 'flex', gap: '8px' }}>
              {[
                { value: 'small', label: 'Small' },
                { value: 'medium', label: 'Medium' },
                { value: 'large', label: 'Large' },
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => updateSetting('ai_summary_button_size', opt.value)}
                  style={{
                    flex: 1, padding: '8px 4px', borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontWeight: '700', border: 'none',
                    background: widget.settings.ai_summary_button_size === opt.value ? '#ED5A29' : '#F3F4F6',
                    color: widget.settings.ai_summary_button_size === opt.value ? 'white' : '#6B7280',
                    transition: 'all 0.2s'
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Alignment */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <span style={{ fontSize: '0.85em', fontWeight: 'bold', color: '#4F4F4F' }}>Alignment</span>
            <div style={{ display: 'flex', gap: '8px' }}>
              {[
                { value: 'left', label: '⬛ Left' },
                { value: 'center', label: '⬛ Center' },
                { value: 'right', label: '⬛ Right' },
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => updateSetting('ai_summary_alignment', opt.value)}
                  style={{
                    flex: 1, padding: '8px 4px', borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontWeight: '700', border: 'none',
                    background: widget.settings.ai_summary_alignment === opt.value ? '#ED5A29' : '#F3F4F6',
                    color: widget.settings.ai_summary_alignment === opt.value ? 'white' : '#6B7280',
                    transition: 'all 0.2s'
                  }}
                >
                  {opt.value.charAt(0).toUpperCase() + opt.value.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'targeting' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <span style={{ fontSize: '0.85em', fontWeight: 'bold', color: '#4F4F4F' }}>Targeting Rule</span>
            <select
              value={widget.rule_id || ''}
              onChange={e => setWidget(prev => ({ ...prev, rule_id: e.target.value }))}
              style={{ padding: '12px', borderRadius: '8px', border: '1px solid #D0D0D0', background: '#fff', fontFamily: 'inherit', fontSize: '14px' }}
            >
              {rules.map(rule => (
                <option key={rule.id} value={rule.id}>{rule.name}</option>
              ))}
            </select>
          </div>
        </div>
      )}
    </>
  );

  return (
    <WidgetEditorShell
      site={site}
      widgetType={widgetType}
      updateWidgetType={setWidgetType}
      widgetId={widgetId}
      queryWidgetType={queryWidgetType}
      saving={saving}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      onSave={() => saveWidget()}
      onCancel={() => history.push(`/sites/${siteId}/widgets`)}
      hidePreview
      tabs={tabs}
      previewContent={previewContent}
      siteId={siteId}
      showErrorDialog={showErrorDialog}
      setShowErrorDialog={setShowErrorDialog}
      validationErrors={validationErrors}
    >
      {tabContent}
    </WidgetEditorShell>
  );
}
